import type { Team } from './series';

/**
 * v22 BOLT-105/106 — team identity, in ONE place.
 *
 * "Team A"/"Team B" read like spreadsheet columns in the field. Jake:
 * "Perhaps we could go with Orange Team and White Team." Colour is something
 * two people can actually say to each other across a street.
 *
 * This also replaces per-player NAMES entirely (BOLT-106). A name was friction
 * at the door and, worse, invited real personal data the app would then have to
 * protect. The colour IS the identity, so there is nothing to type and nothing
 * to store: a net REDUCTION in what we collect.
 *
 * THE WIRE IS FROZEN. `team: 'A' | 'B'` and the `name` field are unchanged; only
 * the player-facing name is derived here. Same "wire frozen, name caught up"
 * pattern as holdup -> Freeze (v20) and radar -> Radius (v21).
 */
export const TEAM_LABEL: Record<Team, string> = {
  A: 'Orange Team',
  B: 'White Team',
};

/** The player-facing name for a team. */
export function teamLabel(team: Team): string {
  return TEAM_LABEL[team];
}

/**
 * What to show for a roster slot. Teams carry the identity; the legacy
 * role-only path (a single round, and Practice, which is roles not teams)
 * keeps whatever name the server holds.
 */
export function displayName(slot: { name: string } | null | undefined, team?: Team | null): string {
  if (team) return TEAM_LABEL[team];
  return slot?.name ?? '';
}

/**
 * The brand TOKENS each team fill uses. Jake picked these: Orange is a signal
 * fill with bone text, White is a bone fill with slate text (contrasting text
 * required; both clear AA). Named tokens, not values, so this stays the single
 * source both renderers are tested against (web CSS, iOS BrandPalette).
 */
export const TEAM_STYLE: Record<Team, { fill: string; text: string }> = {
  A: { fill: '--signal', text: '--bone' },
  B: { fill: '--bone', text: '--slate' },
};
